import { Shape, WallShape, WallPathShape } from "../types";
import { computeWallPathBounds } from "./shapeFactory";

/**
 * Bake any stored `rotation` on walls / wall-paths into their endpoint
 * coordinates so the shape data is rotation-free.
 *
 * `WallShape` and `WallPathShape` carry their geometry as endpoints
 * (`x`/`y` → `x2`/`y2` for walls; `vertices` for paths). Downstream
 * consumers — floor-fill polygon graph, hover / select bounds, snap
 * matcher, JSON serialization, transformer bbox — read those endpoints
 * as world coords and ignore a stored `rotation`. `handleTransformEnd`
 * bakes rotation for *new* transforms, but legacy maps saved before
 * that fix still carry a non-zero rotation alongside pre-rotation
 * endpoints, so the wall renders rotated visually while everything
 * else lands at the un-rotated position.
 *
 * Apply this at data load time (next to `migrateWallsToPath`) so the
 * fix is owned by one place and we don't smuggle data migrations into
 * UI components' `useEffect`s.
 */
export const bakeWallRotations = (shapes: Shape[]): Shape[] => {
  return shapes.map((shape) => {
    if (shape.type === "wall") {
      return bakeWall(shape as WallShape);
    }
    if (shape.type === "wall-path") {
      return bakeWallPath(shape as WallPathShape);
    }
    return shape;
  });
};

const bakeWall = (wall: WallShape): WallShape => {
  const rot = wall.rotation || 0;
  const sX = wall.scaleX ?? 1;
  const sY = wall.scaleY ?? 1;
  if (rot === 0 && sX === 1 && sY === 1) return wall;
  const rad = (rot * Math.PI) / 180;
  const cosR = Math.cos(rad);
  const sinR = Math.sin(rad);
  // Match handleTransformEnd's pipeline: scale the local delta first,
  // then rotate. Legacy walls that carry both rotation and a non-unit
  // scale would otherwise land at the wrong endpoints.
  const dx = (wall.x2 - wall.x) * sX;
  const dy = (wall.y2 - wall.y) * sY;
  return {
    ...wall,
    x2: wall.x + dx * cosR - dy * sinR,
    y2: wall.y + dx * sinR + dy * cosR,
    rotation: 0,
    scaleX: 1,
    scaleY: 1
  };
};

const bakeWallPath = (path: WallPathShape): WallPathShape => {
  const rot = path.rotation || 0;
  const sX = path.scaleX ?? 1;
  const sY = path.scaleY ?? 1;
  if (rot === 0 && sX === 1 && sY === 1) return path;
  const rad = (rot * Math.PI) / 180;
  const cosR = Math.cos(rad);
  const sinR = Math.sin(rad);
  const originX = path.x;
  const originY = path.y;
  // Match handleTransformEnd: scale the local offset first, then rotate.
  const newVertices = path.vertices.map((v) => {
    const lx = (v.x - originX) * sX;
    const ly = (v.y - originY) * sY;
    return {
      ...v,
      x: originX + lx * cosR - ly * sinR,
      y: originY + lx * sinR + ly * cosR
    };
  });
  const bounds = computeWallPathBounds(newVertices);
  return {
    ...path,
    vertices: newVertices,
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    rotation: 0,
    scaleX: 1,
    scaleY: 1
  };
};
